import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CopyIcon,
  DownloadIcon,
  GitForkIcon,
  PencilIcon,
  PinIcon,
  PowerIcon,
  Share2Icon,
  Trash2Icon,
  WandSparklesIcon,
} from 'lucide-react';
import { useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { CopyToWorkspacesDialog } from './CopyToWorkspacesDialog.js';
import { ShareToWorkspaceDialog } from './ShareToWorkspaceDialog.js';
import type { CopyTarget } from './useSkillCopyTargets.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Switch } from '@/components/ui/switch.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  ApiError,
  deleteSkillResource,
  downloadSkillResource,
  fetchSkillDetail,
  fetchSkillExportBlob,
  lockSkillForks,
  unlockSkillForks,
  updateSkill,
  uploadSkillResource,
  type Skill,
} from '@/lib/api/skills.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { forkLockCaption } from './fork-lock.js';
import { isTriggerValid, triggerErrorMessage } from './frontmatter.js';
import {
  hasVersions,
  SKILL_CHANGED_MESSAGE,
  SkillVersions,
} from './SkillVersions.js';
import { TagInput } from './TagInput.js';

interface SkillDetailProps {
  skill: Skill;
  onBack: () => void;
  onUseSkill: (skill: Skill) => void;
  onTogglePin: () => void;
  pinPending: boolean;
  onFork: () => void;
  /**
   * Opens the caller's own private same-trigger copy of this corporate skill,
   * when one exists (typical after an admin promoted it, or after an earlier
   * fork). Non-null replaces the Fork button — a second fork of the same
   * trigger can only 409.
   */
  onOpenOwnCopy: (() => void) | null;
  /** Flips the pause lever (disable ⇄ enable) on a skill the caller manages. */
  onToggleDisabled: () => void;
  disablePending: boolean;
  /** Flips this user's personal mute on a published (corporate or workspace) skill. */
  onToggleMuted: () => void;
  mutePending: boolean;
  onDelete: () => void;
  /**
   * Whether the caller may edit/disable/delete THIS skill: their own private one, or a workspace
   * skill in a workspace they can author in. Decided by the pane, which knows the caller's
   * workspace role; the server enforces the same rule.
   */
  canManage: boolean;
  /**
   * Copies this private skill into the selected workspace, keeping the caller's own copy unless
   * `keepCopy` is false. Null when that isn't on offer (not a private skill, or the caller can't
   * author there).
   */
  onShareToWorkspace: ((keepCopy: boolean) => void) | null;
  /**
   * Copies this workspace skill into the picked workspaces. Null when that isn't on offer (not a
   * workspace skill, or the caller does not manage it).
   */
  onCopyToWorkspaces: ((targets: CopyTarget[]) => void) | null;
  /** The selected workspace's name — labels the Share action. */
  workspaceName: string | null;
  /** Opens the skills list filtered by `tag`; fired by a tag in the read view. */
  onTagClick: (tag: string) => void;
  /**
   * Opens a new chat with the skill creator, prefilled to improve this skill. Null hides the button:
   * on the creator itself, or when the creator is not available to the caller.
   */
  onImproveInChat: (() => void) | null;
}

/**
 * Triggers a same-tab download from an in-memory Blob. A plain
 * `<a href={exportSkillUrl(id)} download>` can't carry the bearer JWT
 * (every `/v1` route is zero-trust JWT-gated — see `RequireAuth()` on
 * `SkillsController`), so the export action fetches the bytes first
 * (`fetchSkillExportBlob`) and downloads them via a synthetic object-URL
 * anchor, mirroring `fetchAttachmentBlob` in `integration-api.ts`.
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  /** Deferred so the browser can start the download first — same idiom as runReportPdf. */
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Read-only detail view for one skill, shown inline in `SkillsPane` in place
 * of the list. Two different questions gate the actions, and conflating them
 * was the bug workspace scope would have introduced: `own` ("is this MY
 * private skill") decides what the caller can copy or silence for themselves —
 * Fork and the personal mute are offered on anything published TO them — while
 * `canManage` decides what they can change for other people (Edit / Disable /
 * Delete), which for a workspace skill depends on their role in that workspace.
 */
export function SkillDetail({
  skill: listSkill,
  onBack,
  onUseSkill,
  onTogglePin,
  pinPending,
  onFork,
  onOpenOwnCopy,
  onToggleDisabled,
  disablePending,
  onToggleMuted,
  mutePending,
  onDelete,
  canManage,
  onShareToWorkspace,
  onCopyToWorkspaces,
  workspaceName,
  onTagClick,
  onImproveInChat,
}: SkillDetailProps): JSX.Element {
  /**
   * List rows never carry `can_lock_forks`; only `GET /v1/skills/:id` does. Only a workspace skill can
   * be locked, so no other scope pays for the extra request. Invalidating `['skills']` refreshes it.
   */
  const { data: skillDetail } = useQuery({
    queryKey: ['skills', 'detail', listSkill.id],
    queryFn: () => fetchSkillDetail(listSkill.id),
    enabled: listSkill.scope === 'workspace',
  });
  /**
   * The detail row is fresher than the list row, so its fork-lock fields win once loaded.
   */
  const skill: Skill = !skillDetail
    ? listSkill
    : {
        ...listSkill,
        forks_locked: skillDetail.forks_locked,
        can_copy: skillDetail.can_copy,
        fork_lock_author_name: skillDetail.fork_lock_author_name,
      };
  const own = skill.scope === 'private';
  const forkLocked = skill.can_copy === false;
  const scopeLabel = own
    ? 'Personal'
    : skill.scope === 'workspace'
      ? 'Workspace'
      : 'Company';
  const pinned = skill.pinned_at !== null;
  const disabled = skill.disabled_at !== null;
  const muted = skill.muted_at !== null;
  const qc = useQueryClient();
  const canLockForks = skillDetail?.can_lock_forks === true;

  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [title, setTitle] = useState(skill.title);
  const [trigger, setTrigger] = useState(skill.trigger);
  const [description, setDescription] = useState(skill.description ?? '');
  const [instructions, setInstructions] = useState(skill.instructions);
  const [tags, setTags] = useState<string[]>(skill.tags ?? []);
  const [triggerConflict, setTriggerConflict] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingShare, setConfirmingShare] = useState(false);
  const [pickingCopyTargets, setPickingCopyTargets] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [forkSwitchPending, setForkSwitchPending] = useState(false);
  const [attachmentPending, setAttachmentPending] = useState(false);
  const [replaceCandidate, setReplaceCandidate] = useState<File | null>(null);

  function startEdit(): void {
    setTitle(skill.title);
    setTrigger(skill.trigger);
    setDescription(skill.description ?? '');
    setInstructions(skill.instructions);
    setTags(skill.tags ?? []);
    setTriggerConflict(false);
    setEditing(true);
  }

  /** Mirrors `SkillEditorDialog`'s `handleTriggerChange` — clears a stale 409 as soon as the user edits the value away from the taken one. */
  function handleTriggerChange(value: string): void {
    setTrigger(value);
    setTriggerConflict(false);
  }

  /**
   * Saves the edited fields. A 409 with a `head` means someone changed the skill meanwhile: the form
   * closes and the latest version is shown. Any other 409 means this trigger is already taken —
   * mapped onto the same inline message `SkillEditorDialog`'s write form shows, via the shared
   * `triggerErrorMessage` helper. Every other failure is already toasted by `assertOk` in `skills.ts`, so the catch-all
   * here only keeps the form open on a save that never actually happened — it must not add a
   * second, more generic toast on top of that one.
   */
  async function saveEdit(): Promise<void> {
    setSavingEdit(true);
    setTriggerConflict(false);
    try {
      await updateSkill(skill.id, {
        title,
        trigger,
        description,
        instructions,
        tags,
      });
      await qc.invalidateQueries({ queryKey: ['skills'] });
      setEditing(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        if (error.head !== null) {
          notify.error(SKILL_CHANGED_MESSAGE);
          setEditing(false);
          await qc.invalidateQueries({ queryKey: ['skills'] });
        } else {
          setTriggerConflict(true);
        }
      }
    } finally {
      setSavingEdit(false);
    }
  }

  /**
   * Flips the fork lock (the switch is "Allow forks", so on means unlocked). Refetches the skills
   * so the detail and the list both show the new state; a failure toasts one fixed message.
   */
  async function toggleForksAllowed(allowForks: boolean): Promise<void> {
    setForkSwitchPending(true);
    try {
      if (allowForks) {
        await unlockSkillForks(skill.id);
      } else {
        await lockSkillForks(skill.id);
      }
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
      notify.error('Could not change forking');
    } finally {
      setForkSwitchPending(false);
    }
  }

  /**
   * Uploads one file to this skill immediately (unlike the text fields, which
   * stage until Save) — `uploadSkillResource` returns the refreshed row and
   * the invalidated skills query re-renders the manifest. Failures are
   * already toasted by `assertOk`, so the empty catch only swallows the
   * rejection. The server upserts on a matching path, so this is safe to call
   * for both a new file and a confirmed replace.
   */
  async function uploadResource(file: File): Promise<void> {
    setAttachmentPending(true);
    try {
      await uploadSkillResource(skill.id, file);
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
    } finally {
      setAttachmentPending(false);
    }
  }

  /**
   * Entry point for a picked file. A name collision with an existing manifest
   * path asks for confirmation first (the server would otherwise silently
   * replace it) — everything else uploads right away.
   */
  function handleAttach(file: File): void {
    const collides = (skill.resources ?? []).some(
      (resource) => resource.path === file.name,
    );
    if (collides) {
      setReplaceCandidate(file);
    } else {
      void uploadResource(file);
    }
  }

  /** Detaches one resource immediately — same immediate-mutation semantics and toast handling as `uploadResource`. */
  async function handleDetach(path: string): Promise<void> {
    setAttachmentPending(true);
    try {
      await deleteSkillResource(skill.id, path);
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
    } finally {
      setAttachmentPending(false);
    }
  }

  /**
   * Downloads one resource's bytes and saves them via the same object-URL
   * anchor as `handleExport`. `downloadSkillResource` already toasts on
   * failure, so the empty catch just skips the save.
   */
  async function handleDownloadResource(path: string): Promise<void> {
    try {
      const blob = await downloadSkillResource(skill.id, path);
      downloadBlob(blob, path.split('/').pop() ?? path);
    } catch {}
  }

  /**
   * Downloads the export bytes. `fetchSkillExportBlob` already toasts on
   * failure (see `saveEdit`'s JSDoc), so the empty catch just skips the
   * download instead of triggering it with no data.
   */
  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      const { blob, filename } = await fetchSkillExportBlob(skill.id);
      downloadBlob(blob, filename);
    } catch {
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 px-4 pt-4 md:px-6">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onBack}
          aria-label="Back to skills"
        >
          <ArrowLeftIcon
            className="size-4"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </Button>
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {skill.title}
        </h3>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 md:px-6">
        {editing ? (
          <SkillEditForm
            title={title}
            trigger={trigger}
            description={description}
            instructions={instructions}
            tags={tags}
            resources={skill.resources ?? []}
            attachmentPending={attachmentPending}
            triggerError={triggerErrorMessage(
              trigger,
              triggerConflict,
              skill.trigger,
            )}
            existingTrigger={skill.trigger}
            onTitleChange={setTitle}
            onTriggerChange={handleTriggerChange}
            onDescriptionChange={setDescription}
            onInstructionsChange={setInstructions}
            onTagsChange={setTags}
            onAttach={handleAttach}
            onDetach={(path) => void handleDetach(path)}
            onDownload={
              forkLocked ? null : (path) => void handleDownloadResource(path)
            }
            onCancel={() => setEditing(false)}
            onSave={() => void saveEdit()}
            saving={savingEdit}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={own ? 'secondary' : 'info'}>{scopeLabel}</Badge>
              {canManage && skill.disabled_at !== null && (
                <Badge variant="destructive">Disabled</Badge>
              )}
              {!own && skill.muted_at !== null && (
                <Badge variant="destructive">Muted</Badge>
              )}
              {skill.forked_from_id !== null && (
                <Badge variant="outline">Forked</Badge>
              )}
              {(skill.tags ?? []).map((tag) => (
                <Badge key={tag} variant="outline" asChild>
                  <button
                    type="button"
                    aria-label={`Show skills tagged ${tag}`}
                    onClick={() => onTagClick(tag)}
                    className="cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {tag}
                  </button>
                </Badge>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">/{skill.trigger}</p>

            {skill.description && (
              <p className="text-sm text-foreground">{skill.description}</p>
            )}

            <div className="flex flex-col gap-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Instructions
              </h4>
              <p className="whitespace-pre-wrap rounded-lg border border-border/40 bg-muted/20 p-3 text-xs leading-relaxed text-foreground">
                {skill.instructions}
              </p>
            </div>

            {skill.resources && skill.resources.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Resources
                </h4>
                <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {skill.resources.map((resource) => (
                    <li
                      key={resource.path}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{resource.path}</span>
                      {!forkLocked && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Download ${resource.path}`}
                          onClick={() =>
                            void handleDownloadResource(resource.path)
                          }
                        >
                          <DownloadIcon
                            className="size-3.5"
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canManage && hasVersions(skill) && <SkillVersions skill={skill} />}
          </>
        )}
      </div>

      {!editing && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/40 px-4 py-3 md:px-6">
          <Button
            size="sm"
            onClick={() => onUseSkill(skill)}
            disabled={disabled || muted}
          >
            Use
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onTogglePin}
            disabled={pinPending}
          >
            <PinIcon
              className={cn(
                'size-3.5',
                pinned && 'fill-current text-foreground',
              )}
              strokeWidth={1.75}
              aria-hidden="true"
            />
            {pinned ? 'Unpin' : 'Pin'}
          </Button>
          {onImproveInChat && (
            <Button variant="outline" size="sm" onClick={onImproveInChat}>
              <WandSparklesIcon
                className="size-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              Improve in chat
            </Button>
          )}
          {!own &&
            (onOpenOwnCopy ? (
              <Button variant="outline" size="sm" onClick={onOpenOwnCopy}>
                <GitForkIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                Open my copy
              </Button>
            ) : (
              <span title={forkLocked ? forkLockCaption(skill) : undefined}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFork}
                  disabled={forkLocked}
                  aria-label={forkLocked ? forkLockCaption(skill) : undefined}
                >
                  <GitForkIcon
                    className="size-3.5"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  Fork
                </Button>
              </span>
            ))}
          {!own && (
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleMuted}
              disabled={mutePending}
            >
              <PowerIcon
                className="size-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              {muted ? 'Enable for me' : 'Disable for me'}
            </Button>
          )}
          {onShareToWorkspace !== null && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmingShare(true)}
            >
              <Share2Icon
                className="size-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              {workspaceName !== null
                ? `Share to ${workspaceName}`
                : 'Share to workspace'}
            </Button>
          )}
          {onCopyToWorkspaces !== null && (
            <span title={forkLocked ? forkLockCaption(skill) : undefined}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickingCopyTargets(true)}
                disabled={forkLocked}
              >
                <CopyIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                Copy to workspace
              </Button>
            </span>
          )}
          {!forkLocked && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              <DownloadIcon
                className="size-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          )}
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={startEdit}>
                <PencilIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleDisabled}
                disabled={disablePending}
              >
                <PowerIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                {disabled ? 'Enable' : 'Disable'}
              </Button>
              <Button
                variant="destructive-outline"
                size="sm"
                onClick={() => setConfirmingDelete(true)}
              >
                <Trash2Icon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                Delete
              </Button>
            </>
          )}
        </div>
      )}

      {canLockForks && (
        <div className="flex items-start gap-3 px-4 pb-3 md:px-6">
          <Switch
            id={`allow-forks-${skill.id}`}
            checked={!skill.forks_locked}
            disabled={forkSwitchPending}
            onCheckedChange={(allowForks) =>
              void toggleForksAllowed(allowForks)
            }
          />
          <div className="flex flex-col">
            <label
              htmlFor={`allow-forks-${skill.id}`}
              className="text-sm font-medium"
            >
              Allow forks
            </label>
            <span className="text-xs text-muted-foreground">
              When off, others can use this skill but cannot copy it.
            </span>
          </div>
        </div>
      )}

      {onShareToWorkspace !== null && (
        <ShareToWorkspaceDialog
          open={confirmingShare}
          onOpenChange={setConfirmingShare}
          trigger={skill.trigger}
          workspaceName={workspaceName}
          onShare={onShareToWorkspace}
        />
      )}

      {onCopyToWorkspaces !== null && (
        <CopyToWorkspacesDialog
          open={pickingCopyTargets}
          onOpenChange={setPickingCopyTargets}
          trigger={skill.trigger}
          sourceWorkspaceId={skill.workspace_id}
          onCopy={onCopyToWorkspaces}
        />
      )}

      <AppDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete skill?"
        description="This cannot be undone."
      >
        <div className="mt-3 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Deleting &ldquo;{skill.title}&rdquo; removes it permanently. The
            agent will no longer resolve /{skill.trigger}.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-outline"
              size="sm"
              onClick={() => {
                setConfirmingDelete(false);
                onDelete();
              }}
            >
              Delete skill
            </Button>
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={replaceCandidate !== null}
        onOpenChange={(open) => {
          if (!open) setReplaceCandidate(null);
        }}
        title="Replace existing file?"
        description="This replaces the attachment with the same name."
      >
        <div className="mt-3 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            A file named &ldquo;{replaceCandidate?.name}&rdquo; is already
            attached. Uploading replaces it.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReplaceCandidate(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const file = replaceCandidate;
                setReplaceCandidate(null);
                if (file) void uploadResource(file);
              }}
            >
              Replace
            </Button>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}

interface SkillEditFormProps {
  title: string;
  trigger: string;
  description: string;
  instructions: string;
  tags: string[];
  /** Current manifest — attachments mutate immediately (see `handleAttach`), so this is always the live server state. */
  resources: NonNullable<Skill['resources']>;
  attachmentPending: boolean;
  /** Live validation message — a format mismatch or a 409 mapped by the caller — mirrors `SkillWriteForm`'s `triggerError`. */
  triggerError: string | null;
  /** The skill's stored trigger: kept valid in its old shape while unchanged. */
  existingTrigger: string;
  onTitleChange: (value: string) => void;
  onTriggerChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onInstructionsChange: (value: string) => void;
  onTagsChange: (tags: string[]) => void;
  onAttach: (file: File) => void;
  onDetach: (path: string) => void;
  /** Null hides the per-file download control (copies are off for this viewer). */
  onDownload: ((path: string) => void) | null;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}

/** Inline edit form for an own private skill — the wire `PATCH` fields plus immediate attachment management. */
function SkillEditForm({
  title,
  trigger,
  description,
  instructions,
  tags,
  resources,
  attachmentPending,
  triggerError,
  existingTrigger,
  onTitleChange,
  onTriggerChange,
  onDescriptionChange,
  onInstructionsChange,
  onTagsChange,
  onAttach,
  onDetach,
  onDownload,
  onCancel,
  onSave,
  saving,
}: SkillEditFormProps): JSX.Element {
  const canSave =
    title.trim().length > 0 &&
    isTriggerValid(trigger, existingTrigger) &&
    instructions.trim().length > 0;
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Title
        <Input
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Trigger
        <Input
          value={trigger}
          onChange={(event) => onTriggerChange(event.target.value)}
          aria-invalid={triggerError !== null}
        />
        {triggerError !== null && (
          <span className="text-xs text-destructive">{triggerError}</span>
        )}
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Description
        <Input
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </label>
      <div className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Tags
        <TagInput value={tags} onChange={onTagsChange} />
      </div>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Instructions
        <Textarea
          value={instructions}
          onChange={(event) => onInstructionsChange(event.target.value)}
          className="min-h-32"
        />
      </label>
      <div className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
        Attachments
        {resources.length > 0 && (
          <ul className="flex flex-col gap-1">
            {resources.map((resource) => (
              <li
                key={resource.path}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate font-normal">{resource.path}</span>
                <div className="flex items-center gap-1">
                  {onDownload !== null && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Download ${resource.path}`}
                      onClick={() => onDownload(resource.path)}
                    >
                      <DownloadIcon
                        className="size-3.5"
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${resource.path}`}
                    disabled={attachmentPending}
                    onClick={() => onDetach(resource.path)}
                  >
                    <Trash2Icon
                      className="size-3.5"
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={attachmentInputRef}
          type="file"
          data-testid="skill-attachment-input"
          className="hidden"
          onChange={(event) => {
            const [file] = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (file) onAttach(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={attachmentPending}
          onClick={() => attachmentInputRef.current?.click()}
        >
          {attachmentPending ? 'Working…' : 'Add file…'}
        </Button>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving || !canSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
